import {Grid} from '@/models/Grid';
import {Bar} from '@/models/Bar';

export class Cell {
	
	private _grid: Grid;
	private _x: number;
	private _y: number;
	private _enable: boolean = false;
	private _bar: Bar = new Bar(this);
	
	constructor(grid: Grid, x: number, y: number) {
		this._grid = grid;
		this._x = x;
		this._y = y;
	}
	
	public get grid(): Grid {
		return this._grid;
	}
	
	public get x(): number {
		return this._x;
	}
	
	public get y(): number {
		return this._y;
	}
	
	public get bar(): Bar {
		return this._bar;
	}
	
	public get enable(): boolean {
		return this._enable;
	}
	
	public set enable(value: boolean) {
		this.fix();
		const old = this._enable;
		this._enable = value;
		if (!old && value) {
			this.fixArround();
		}
	}
	
	public get top(): Cell {
		return this.grid.getCell(this.x, this.y - 1);
	}
	
	public get bottom(): Cell {
		return this.grid.getCell(this.x, this.y + 1);
	}
	
	public get left(): Cell {
		return this.grid.getCell(this.x - 1, this.y);
	}
	
	public get right(): Cell {
		return this.grid.getCell(this.x + 1, this.y);
	}
	
	public get topLeft(): Cell {
		return this.grid.getCell(this.x - 1, this.y - 1);
	}
	
	public get bottomLeft(): Cell {
		return this.grid.getCell(this.x - 1, this.y + 1);
	}
	
	public get topRight(): Cell {
		return this.grid.getCell(this.x + 1, this.y - 1);
	}
	
	public get bottomRight(): Cell {
		return this.grid.getCell(this.x + 1, this.y + 1);
	}
	
	public get selected(): boolean {
		return this.grid.selected === this;
	}
	
	public set selected(value: boolean) {
		this.fix();
		if (!value && this.selected) {
			this.grid.selected = null;
		} else if (value) {
			this.grid.selected = this;
		}
	}
	
	public fix(): void {
		this.grid.fix(this);
	}
	
	public fixArround(): void {
		this.grid.add(this.x - 1, this.y, false);
		this.grid.add(this.x + 1, this.y, false);
		this.grid.add(this.x, this.y - 1, false);
		this.grid.add(this.x, this.y + 1, false);
		
		this.grid.add(this.x + 1, this.y + 1, false);
		this.grid.add(this.x + 1, this.y - 1, false);
		this.grid.add(this.x - 1, this.y + 1, false);
		this.grid.add(this.x - 1, this.y - 1, false);
	}
	
	public get barrableLeft(): boolean {
		const cellLeft1 = this.grid.getCell(this.x - 1, this.y);
		const cellLeft2 = this.grid.getCell(this.x - 2, this.y);
		const cellLeft3 = this.grid.getCell(this.x - 3, this.y);
		const cellLeft4 = this.grid.getCell(this.x - 4, this.y);
		
		const sum = 0 +
			<any>this.enable +
			<any>cellLeft1.enable +
			<any>cellLeft2.enable +
			<any>cellLeft3.enable +
			<any>cellLeft4.enable
		;
		
		if (
			sum >= 4 &&
			!cellLeft1.bar.right &&
			!cellLeft2.bar.right &&
			!cellLeft3.bar.right &&
			!cellLeft4.bar.right
		) {
			return  true;
		}
		return false;
	}
	
	public get barrableRight(): boolean {
		
		const cellRight1 = this.grid.getCell(this.x + 1, this.y);
		const cellRight2 = this.grid.getCell(this.x + 2, this.y);
		const cellRight3 = this.grid.getCell(this.x + 3, this.y);
		const cellRight4 = this.grid.getCell(this.x + 4, this.y);
		
		const sum = 0 +
			<any>this.enable +
			<any>cellRight1.enable +
			<any>cellRight2.enable +
			<any>cellRight3.enable +
			<any>cellRight4.enable
		;
		
		if (
			sum >= 4 &&
			!cellRight1.bar.left &&
			!cellRight2.bar.left &&
			!cellRight3.bar.left &&
			!cellRight4.bar.left
		) {
			return  true;
		}
		return false;
	}
	
	public get barrableTop(): boolean {
		
		const cellTop1 = this.grid.getCell(this.x, this.y - 1);
		const cellTop2 = this.grid.getCell(this.x, this.y - 2);
		const cellTop3 = this.grid.getCell(this.x, this.y - 3);
		const cellTop4 = this.grid.getCell(this.x, this.y - 4);
		
		const sum = 0 +
			<any>this.enable +
			<any>cellTop1.enable +
			<any>cellTop2.enable +
			<any>cellTop3.enable +
			<any>cellTop4.enable
		;
		
		if (
			sum >= 4 &&
			!cellTop1.bar.bottom &&
			!cellTop2.bar.bottom &&
			!cellTop3.bar.bottom &&
			!cellTop4.bar.bottom
		) {
			return  true;
		}
		return false;
	}
	
	public get barrableBottom(): boolean {
		
		const cellBottom1 = this.grid.getCell(this.x, this.y + 1);
		const cellBottom2 = this.grid.getCell(this.x, this.y + 2);
		const cellBottom3 = this.grid.getCell(this.x, this.y + 3);
		const cellBottom4 = this.grid.getCell(this.x, this.y + 4);
		
		const sum = 0 +
			<any>this.enable +
			<any>cellBottom1.enable +
			<any>cellBottom2.enable +
			<any>cellBottom3.enable +
			<any>cellBottom4.enable
		;
		
		if (
			sum >= 4 &&
			!cellBottom1.bar.top &&
			!cellBottom2.bar.top &&
			!cellBottom3.bar.top &&
			!cellBottom4.bar.top
		) {
			return  true;
		}
		return false;
	}
	
	public get barrableTopRight(): boolean {
		
		const cellTR1 = this.grid.getCell(this.x + 1, this.y - 1);
		const cellTR2 = this.grid.getCell(this.x + 2, this.y - 2);
		const cellTR3 = this.grid.getCell(this.x + 3, this.y - 3);
		const cellTR4 = this.grid.getCell(this.x + 4, this.y - 4);
		
		const sum = 0 +
			<any>this.enable +
			<any>cellTR1.enable +
			<any>cellTR2.enable +
			<any>cellTR3.enable +
			<any>cellTR4.enable
		;
		
		if (
			sum >= 4 &&
			!cellTR1.bar.bottomLeft &&
			!cellTR2.bar.bottomLeft &&
			!cellTR3.bar.bottomLeft &&
			!cellTR4.bar.bottomLeft
		) {
			return  true;
		}
		return false;
	}
	
	public get barrableTopLeft(): boolean {
		
		const cellTL1 = this.grid.getCell(this.x - 1, this.y - 1);
		const cellTL2 = this.grid.getCell(this.x - 2, this.y - 2);
		const cellTL3 = this.grid.getCell(this.x - 3, this.y - 3);
		const cellTL4 = this.grid.getCell(this.x - 4, this.y - 4);
		
		const sum = 0 +
			<any>this.enable +
			<any>cellTL1.enable +
			<any>cellTL2.enable +
			<any>cellTL3.enable +
			<any>cellTL4.enable
		;
		
		if (
			sum >= 4 &&
			!cellTL1.bar.bottomRight &&
			!cellTL2.bar.bottomRight &&
			!cellTL3.bar.bottomRight &&
			!cellTL4.bar.bottomRight
		) {
			return  true;
		}
		return false;
	}
	
	public get barrableBottomRight(): boolean {
		
		const cell1 = this.grid.getCell(this.x + 1, this.y + 1);
		const cell2 = this.grid.getCell(this.x + 2, this.y + 2);
		const cell3 = this.grid.getCell(this.x + 3, this.y + 3);
		const cell4 = this.grid.getCell(this.x + 4, this.y + 4);
		
		const sum = 0 +
			<any>this.enable +
			<any>cell1.enable +
			<any>cell2.enable +
			<any>cell3.enable +
			<any>cell4.enable
		;
		
		if (
			sum >= 4 &&
			!cell1.bar.topLeft &&
			!cell2.bar.topLeft &&
			!cell3.bar.topLeft &&
			!cell4.bar.topLeft
		) {
			return  true;
		}
		return false;
	}
	
	public get barrableBottomLeft(): boolean {
		
		const cell1 = this.grid.getCell(this.x - 1, this.y + 1);
		const cell2 = this.grid.getCell(this.x - 2, this.y + 2);
		const cell3 = this.grid.getCell(this.x - 3, this.y + 3);
		const cell4 = this.grid.getCell(this.x - 4, this.y + 4);
		
		const sum = 0 +
			<any>this.enable +
			<any>cell1.enable +
			<any>cell2.enable +
			<any>cell3.enable +
			<any>cell4.enable
		;
		
		if (
			sum >= 4 &&
			!cell1.bar.topRight &&
			!cell2.bar.topRight &&
			!cell3.bar.topRight &&
			!cell4.bar.topRight
		) {
			return  true;
		}
		return false;
	}
	
	public get isHoverable(): boolean {
		return this.barrableLeft ||
			this.barrableRight ||
			this.barrableTop ||
			this.barrableBottom ||
			
			this.barrableTopRight ||
			this.barrableTopLeft ||
			this.barrableBottomRight ||
			this.barrableBottomLeft
		;
	}
	
	public traceTop(): void {
		console.log('trace top:', this.x, this.y);
		const cell1 = this.grid.getCell(this.x, this.y - 1);
		const cell2 = this.grid.getCell(this.x, this.y - 2);
		const cell3 = this.grid.getCell(this.x, this.y - 3);
		const cell4 = this.grid.getCell(this.x, this.y - 4);
		this.enable = true;
		cell1.enable = true;
		cell2.enable = true;
		cell3.enable = true;
		cell4.enable = true;
		cell1.bar.bottom = true;
		cell2.bar.bottom = true;
		cell3.bar.bottom = true;
		cell4.bar.bottom = true;
	}
	
	public traceBottom(): void {
		console.log('trace bottom:', this.x, this.y);
		const cell1 = this.grid.getCell(this.x, this.y + 1);
		const cell2 = this.grid.getCell(this.x, this.y + 2);
		const cell3 = this.grid.getCell(this.x, this.y + 3);
		const cell4 = this.grid.getCell(this.x, this.y + 4);
		this .enable = true;
		cell1.enable = true;
		cell2.enable = true;
		cell3.enable = true;
		cell4.enable = true;
		this .bar.bottom = true;
		cell1.bar.bottom = true;
		cell2.bar.bottom = true;
		cell3.bar.bottom = true;
	}
	
	public traceLeft(): void {
		console.log('trace left:', this.x, this.y);
		const cell1 = this.grid.getCell(this.x - 1, this.y);
		const cell2 = this.grid.getCell(this.x - 2, this.y);
		const cell3 = this.grid.getCell(this.x - 3, this.y);
		const cell4 = this.grid.getCell(this.x - 4, this.y);
		this .enable = true;
		cell1.enable = true;
		cell2.enable = true;
		cell3.enable = true;
		cell4.enable = true;
		cell1.bar.right = true;
		cell2.bar.right = true;
		cell3.bar.right = true;
		cell4.bar.right = true;
	}
	
	public traceRight(): void {
		console.log('trace right:', this.x, this.y);
		const cell1 = this.grid.getCell(this.x + 1, this.y);
		const cell2 = this.grid.getCell(this.x + 2, this.y);
		const cell3 = this.grid.getCell(this.x + 3, this.y);
		const cell4 = this.grid.getCell(this.x + 4, this.y);
		this .enable = true;
		cell1.enable = true;
		cell2.enable = true;
		cell3.enable = true;
		cell4.enable = true;
		this .bar.right = true;
		cell1.bar.right = true;
		cell2.bar.right = true;
		cell3.bar.right = true;
	}
	
	public traceTopRight(): void {
		console.log('trace top right:', this.x, this.y);
		const cell1 = this.grid.getCell(this.x + 1, this.y - 1);
		const cell2 = this.grid.getCell(this.x + 2, this.y - 2);
		const cell3 = this.grid.getCell(this.x + 3, this.y - 3);
		const cell4 = this.grid.getCell(this.x + 4, this.y - 4);
		this .enable = true;
		cell1.enable = true;
		cell2.enable = true;
		cell3.enable = true;
		cell4.enable = true;
		cell1.bar.bottomLeft = true;
		cell2.bar.bottomLeft = true;
		cell3.bar.bottomLeft = true;
		cell4.bar.bottomLeft = true;
	}
	
	
	public traceTopLeft(): void {
		console.log('trace top right:', this.x, this.y);
		const cell1 = this.grid.getCell(this.x - 1, this.y - 1);
		const cell2 = this.grid.getCell(this.x - 2, this.y - 2);
		const cell3 = this.grid.getCell(this.x - 3, this.y - 3);
		const cell4 = this.grid.getCell(this.x - 4, this.y - 4);
		this .enable = true;
		cell1.enable = true;
		cell2.enable = true;
		cell3.enable = true;
		cell4.enable = true;
		cell1.bar.bottomRight = true;
		cell2.bar.bottomRight = true;
		cell3.bar.bottomRight = true;
		cell4.bar.bottomRight = true;
	}
	
	public traceBottomRight(): void {
		console.log('trace bottom right:', this.x, this.y);
		const cell1 = this.grid.getCell(this.x + 1, this.y + 1);
		const cell2 = this.grid.getCell(this.x + 2, this.y + 2);
		const cell3 = this.grid.getCell(this.x + 3, this.y + 3);
		const cell4 = this.grid.getCell(this.x + 4, this.y + 4);
		this .enable = true;
		cell1.enable = true;
		cell2.enable = true;
		cell3.enable = true;
		cell4.enable = true;
		cell1.bar.topLeft = true;
		cell2.bar.topLeft = true;
		cell3.bar.topLeft = true;
		cell4.bar.topLeft = true;
	}
	
	
	public traceBottomLeft(): void {
		console.log('trace bottom left:', this.x, this.y);
		const cell1 = this.grid.getCell(this.x - 1, this.y + 1);
		const cell2 = this.grid.getCell(this.x - 2, this.y + 2);
		const cell3 = this.grid.getCell(this.x - 3, this.y + 3);
		const cell4 = this.grid.getCell(this.x - 4, this.y + 4);
		this .enable = true;
		cell1.enable = true;
		cell2.enable = true;
		cell3.enable = true;
		cell4.enable = true;
		cell1.bar.topRight = true;
		cell2.bar.topRight = true;
		cell3.bar.topRight = true;
		cell4.bar.topRight = true;
	}
	
	public clone(grid: Grid): Cell {
		const cell = new Cell(grid, this.x, this.y);
		cell._enable = this._enable;
		cell._bar = this._bar.clone(cell);
		return cell;
	}
	
	public toJSON(): any {
		return {
			enable: this._enable,
			bar: this._bar.toJSON(),
			x: this._x,
			y: this._y,
		}
	}
	
	public fromJSON(data: any): this {
		this._enable = data.enable;
		this._bar = (new Bar(this)).fromJSON(data.bar);
		return this;
	}
}